/**
 * BuilderLeg — Shared type for parlay builder legs
 *
 * Used by both web and mobile parlay builders. Contains full context
 * preservation from AI picks including analysis, situational factors,
 * and chart data.
 */

import type { ConfidenceLevel, EdgeStrength } from './aiOutputConstants';
import type { AnalysisConfig } from './analysisConfig';
import type { GeneratedLeg, GeneratedParlay } from './api/picksApi';
import type { SharedParlayLeg } from './hooks/useSharing';
import { type LeagueKey, leagueKeyOrNull } from './league';
import type { LegProvenance } from './legProvenance';

// =============================================================================
// Types
// =============================================================================

/**
 * A leg in the parlay builder with full context preservation
 */
export interface BuilderLeg {
  id: string; // Unique ID for this builder session
  player?: string | null; // Player name (null for team bets)
  playerId?: number | null; // BallDontLie player ID
  team: string; // Team abbreviation
  opponentTeam?: string | null; // Opponent team for matchup display
  isHomeGame?: boolean | null; // True when `team` plays at home
  statType: string; // Normalized stat type: pts, reb, spreads, totals, etc.
  line: number | null; // Line value (null for moneyline)
  overUnder: 'Over' | 'Under' | null; // Direction (null for spreads/moneyline)
  odds: string; // American odds string
  gameId?: string | null; // Odds API game ID
  gameDate?: string | null; // Game date (YYYY-MM-DD)
  gameStartsAt?: string | null; // ISO timestamp of game start (for stale tracking prevention)
  sportsbook: string; // Sportsbook source (locked to first leg's book)
  marketKey?: string | null; // Market key for settlement

  // Context from AI picks (preserved when adding from generated parlays)
  aiAnalysis?: string | null; // AI's reasoning for this pick
  aiConfidence?: ConfidenceLevel | string | null; // High/Medium/Low (parlay-level)
  aiEdge?: EdgeStrength | string | null; // Strong/Moderate/Slight (per-leg edge for scoring)
  aiTier?: string | null; // Risk tier from parlay name (e.g., "Safe Play", "Long Shot")

  // Situational context
  isB2b?: boolean | null; // Is team on back-to-back?
  blowoutRisk?: string | null; // low/medium/high
  matchupNote?: string | null; // e.g., "vs elite defense"
  recentForm?: string | null; // e.g., "hit 4 of last 5"
  injuryContext?: string | null; // e.g., "teammate out, usage boost"

  // Source tracking
  source: 'ai' | 'manual'; // Where this leg came from
  provenance: LegProvenance; // What chose it, sent per leg when tracked
  predictionId?: string; // A generated leg's prediction row id
  factorsUsed?: AnalysisConfig | null; // Toggles a chat or analyzer workflow used

  // Config snapshot — captures analysisConfig at the time this leg was added
  // so factor flags accurately reflect the user's config per-leg, not per-parlay
  analysisConfig?: AnalysisConfig | null;

  // Chart data (preserved from AI picks for stats visualization)
  recentGames?: unknown[]; // Player prop: recent game stats
  h2hGames?: unknown[]; // Player prop: head-to-head vs opponent
  teamRecentGames?: unknown[]; // Game prop: team's recent games
  teamH2hGames?: unknown[]; // Game prop: team's H2H vs opponent
  opponentTeamRecentGames?: unknown[]; // Game total: opponent's recent games
  opponentTeamName?: string | null; // Game total: opponent team name for perspective toggle

  // Enhanced edge display data (from LegExplanation/LegAnalysis for PicksView-style display)
  primaryEdge?: {
    metric?: string;
    playerValue?: number;
    line?: number;
    edge?: number;
    edgePercent?: number;
  } | null;
  supportingFactors?: Array<{
    impact?: string;
    label?: string;
    value?: string | number;
    detail?: string;
  }> | null;
  riskFactors?: Array<{
    severity?: string;
    label?: string;
    detail?: string;
  }> | null;
  confidenceBreakdown?: {
    statisticalEdge?: number;
    matchupFactor?: number;
    consistency?: number;
    gameScript?: number;
    overall?: number;
  } | null;
  trend?: string | null; // Hot/Cold/Consistent
  average?: string | null; // Player's recent average

  // League tracking (for cross-league parlays)
  leagueKey: LeagueKey;
}

/**
 * Result of attempting to add a leg
 */
export interface AddLegResult {
  success: boolean;
  error?: string;
  blocked?: boolean;
}

/**
 * Result of attempting to add multiple legs
 */
export interface AddAllLegsResult {
  addedCount: number;
  blockedCount: number;
  blockedReasons: string[];
}

// =============================================================================
// Conversion
// =============================================================================

/**
 * Convert a GeneratedLeg (from AI picks) to a BuilderLeg (for parlay builder), or null when the
 * parlay names no sportsbook. Preserves AI analysis, situational factors and scoring metadata.
 */
export function parlayLegToBuilderLeg(
  leg: GeneratedLeg,
  parlay: GeneratedParlay,
  leagueKey: LeagueKey,
): BuilderLeg | null {
  const { sportsbook } = parlay;
  if (!sportsbook) return null;

  // Handle playerId conversion (GeneratedLeg allows string | number, BuilderLeg wants number | null)
  let playerId: number | null = null;
  if (typeof leg.playerId === 'number') {
    playerId = leg.playerId;
  } else if (typeof leg.playerId === 'string') {
    const parsed = parseInt(leg.playerId, 10);
    playerId = Number.isNaN(parsed) ? null : parsed;
  }

  return {
    // Unique ID with random suffix to prevent collisions when adding multiple legs quickly
    id: `${parlay.name || 'parlay'}-${leg.player || leg.team}-${leg.prop}-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
    player: leg.player || null,
    playerId,
    team: leg.team || '',
    opponentTeam: leg.opponentTeam || null,
    statType: leg.statType || leg.propType || leg.marketKey || '',
    line: typeof leg.line === 'number' ? leg.line : null,
    overUnder: (leg.overUnder as 'Over' | 'Under' | null) || null,
    odds: leg.odds || '',
    gameId: leg.gameId || null,
    gameDate: leg.gameDate || null,
    gameStartsAt: leg.gameStartsAt || null,
    sportsbook,
    marketKey: leg.marketKey || null,
    // AI context
    aiAnalysis: leg.explanation?.summary || (leg.analysis?.reason as string | null) || null,
    aiConfidence: parlay.confidence || null,
    aiEdge: leg.analysis?.edge || null, // Per-leg edge for scoring
    aiTier: parlay.name || null, // Original parlay name for tier-based scoring
    // Situational context
    isB2b: leg.isB2b ?? null,
    blowoutRisk: leg.blowoutRisk || null,
    isHomeGame: leg.isHomeGame ?? null,
    matchupNote: leg.analysis?.currentOpponent ? `vs ${leg.analysis.currentOpponent}` : null,
    recentForm: null,
    injuryContext: null,
    // Source
    source: 'ai',
    provenance: 'generation',
    predictionId: leg.predictionId,
    // Chart data (preserved for stats visualization)
    recentGames: leg.analysis?.recentGames,
    h2hGames: leg.analysis?.h2hGames,
    teamRecentGames: leg.analysis?.teamRecentGames,
    teamH2hGames: leg.analysis?.teamH2hGames,
    opponentTeamRecentGames: leg.analysis?.opponentTeamRecentGames,
    opponentTeamName: leg.analysis?.opponentTeamName || leg.opponentTeam || null,
    // Enhanced edge display data (from LegExplanation for PicksView-style display)
    primaryEdge: leg.explanation?.primaryEdge || null,
    supportingFactors: leg.explanation?.supportingFactors || null,
    riskFactors: leg.explanation?.riskFactors || null,
    confidenceBreakdown: leg.explanation?.confidenceBreakdown || null,
    trend: leg.analysis?.trend || null,
    average: leg.analysis?.average || null,
    // League tracking
    leagueKey,
  };
}

/**
 * Convert a leg from a parlay someone shared into a BuilderLeg, or null when the leg lacks the
 * stat type, team, league or sportsbook the builder needs.
 */
export function sharedLegToBuilderLeg(leg: SharedParlayLeg): BuilderLeg | null {
  const leagueKey = leagueKeyOrNull(leg.leagueKey);
  if (!leg.statType || !leg.team || !leagueKey || !leg.sportsbook) return null;

  return {
    id: `shared-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
    player: leg.player || null,
    playerId: leg.playerId || null,
    team: leg.team,
    opponentTeam: leg.opponentTeam || null,
    statType: leg.statType,
    line: leg.line ?? null,
    overUnder: leg.overUnder || null,
    odds: leg.odds || '',
    gameId: leg.gameId || null,
    gameDate: leg.gameDate || null,
    sportsbook: leg.sportsbook,
    marketKey: leg.marketKey || null,
    source: 'manual',
    provenance: 'manual',
    leagueKey,
  };
}
